import { Component, inject, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { PermissionService } from '../../../../../../services/permission.service';
import { CandidatesApplied, Mission } from "../../../../../../models/Mission";
import { CandidatStatusOnMission, MissionStatusEnum } from "../../../../../../shared/utils/enums";
import { isMissionExpired } from "../../../../../../shared/utils/utils.functions";
import { candidatAccepted, candidateApplied } from "../../../../../../shared/utils/data/shared.data";
import { SvgIconComponent } from "angular-svg-icon";
import { TooltipModule } from "ngx-bootstrap/tooltip";
import { MissionDetailsComponent } from "../../mission-details/mission-details.component";
import { MatDialog } from "@angular/material/dialog";
import { CustomDialogComponent } from "../../../../../../shared/custom-dialog/custom-dialog.component";
import { EtablissementData } from "../../../../../../shared/utils/data/etablissments-data";
import { MissionService } from "../../../../../../services/mission.service";
import { ActivatedRoute, Router } from "@angular/router";
import { MissionCandidaturesComponent } from "../../mission-candidatures/mission-candidatures.component";
import { SharedModule } from "../../../../../../shared/shared.module";
import { EstablishmentService } from "../../../../../../services/establishment.service";
import { DocuSignService } from "../../../../../../services/docu-sign.service";
import { CandidatService } from '../../../../../../services/candidat.service';
import { ContractPreviewDialogComponent } from '../../../../../../shared/contract-preview-dialog/contract-preview-dialog.component';

@Component({
  selector: 'app-mission-table-actions',
  templateUrl: './mission-table-actions.component.html',
  styleUrl: './mission-table-actions.component.scss',
  imports: [
    SvgIconComponent,
    TooltipModule,
    MissionCandidaturesComponent,
    SharedModule
  ],
  standalone: true
})
export class MissionTableActionsComponent implements OnInit {
  private readonly permissionService = inject(PermissionService);

  /** Droit « Supprimer/Annuler une offre de mission (non encore affectée) » = MISSION_DELETE (miroir du back, qui reste l'autorité). */
  can(permission: string): boolean {
    return this.permissionService.has(permission);
  }

  @Input() data: Mission;
  @Input() column: any;
  expired = false;
  candidatList: CandidatesApplied[] = [];
  @Input() availableCandidates: CandidatesApplied[] = [];

  indexAcceptedCandidate: number;
  statusCandidateAccepted: CandidatStatusOnMission;
  @ViewChild('missionAllCandidaciesDialog') missionAllCandidaciesDialog!: TemplateRef<any>;
  @ViewChild('missionCandidaciesDialog') missionCandidaciesDialog!: TemplateRef<any>;
  loadingContract = false;
  loading = false;
  etbId = 0;

  constructor(private dialog: MatDialog,
    private missionService: MissionService,
    private candidatService: CandidatService,
    private route: ActivatedRoute, private router: Router, private establishmentService: EstablishmentService,
    private docuSign: DocuSignService
  ) {

  }

  ngOnInit(): void {
    this.etbId = this.route.snapshot.params['establishmentId'] ?? 0;
    this.expired = isMissionExpired(this.data.enddate, this.data.endtime);
    this.candidatList = this.data.candidatesApplied;
    this.indexAcceptedCandidate = this.candidatList?.findIndex((c) =>
      candidatAccepted.includes(this.data.statusOfCandidatOnMission) || candidateApplied.includes(this.data.statusOfCandidatOnMission)
    );
    if (this.indexAcceptedCandidate > -1) {
      this.statusCandidateAccepted = this.data.statusOfCandidatOnMission;
    }
    // this.fetchAllCandidates();
  }

  Voir() {
    const candidateId = this.candidatList.find(candidate =>
      candidate.statusOfCandidatOnMission === CandidatStatusOnMission.ACCEPTED ||
      candidate.statusOfCandidatOnMission === CandidatStatusOnMission.ACCEPTED_AND_SIGNED_BY_BOTH)?.candidatId!;

    this.missionService
      .SignerMission(candidateId, this.data.id, this.data.etablissementId)
      .subscribe({
        next: (data) => {
          window.open(data, '_blank', 'noopener, noreferrer');
        },
        error: (err) => console.log(err),
      });
  }

  voirContractDisposition() {
    const candidateId = this.candidatList.find(candidate =>
      candidate.statusOfCandidatOnMission === CandidatStatusOnMission.ACCEPTED ||
      candidate.statusOfCandidatOnMission === CandidatStatusOnMission.ACCEPTED_AND_SIGNED_BY_BOTH)?.candidatId!;

    this.establishmentService
      .generateContractDisposition(candidateId, this.data.id, this.data.etablissementId)
      .subscribe({
        next: (data) => {
          if (data && data.contractBytes) {
            const byteCharacters = atob(data.contractBytes);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank', 'noopener, noreferrer');
          }
        },
        error: (err) => console.log(err),
      });
  }

  isAccepted() {
    return this.data.candidatesApplied.some(item => item.statusOfCandidatOnMission === CandidatStatusOnMission.ACCEPTED);
  }

  /**
   * Mirrors backend cancelPendingMissionByAgency: cancellation is refused once any candidate
   * reached an "affected" status, so we hide the cancel button in exactly those cases.
   */
  canCancelMission(): boolean {
    const blocking = [
      CandidatStatusOnMission.ACCEPTED,
      CandidatStatusOnMission.ACCEPTED_AND_SIGNED_BY_BOTH,
      CandidatStatusOnMission.WAITING_FOR_ESTABLISHMENT_VALIDATION,
      CandidatStatusOnMission.MISSION_VALIDATED_WAITING_FOR_PAYMENT,
      CandidatStatusOnMission.PAYMENT_DONE,
      CandidatStatusOnMission.VALIDATION_REJECTED_BY_ESTABLISHMENT,
      CandidatStatusOnMission.CONTRACT_PENDING_VALIDATION,
      // REJECTED ne bloque plus : un candidat refusé n'empêche pas d'annuler l'offre.
    ];
    return !this.data?.candidatesApplied?.some(
      (c: any) => blocking.includes(c.statusOfCandidatOnMission)
    );
  }

  isContractPendingValidation(): boolean {
    return this.data.candidatesApplied?.some(
      item => item.statusOfCandidatOnMission === CandidatStatusOnMission.CONTRACT_PENDING_VALIDATION
    );
  }

  /**
   * Edit icon should stay visible as long as at least one contract is not sent.
   * - CONTRACT_PENDING_VALIDATION: neither contract sent yet
   * - ACCEPTED: mission contract sent, disposition may still be pending
   * Once both contracts are signed, status becomes ACCEPTED_AND_SIGNED_BY_BOTH — then we hide it.
   */
  canEditContract(): boolean {
    return this.data.candidatesApplied?.some(
      item =>
        item.statusOfCandidatOnMission === CandidatStatusOnMission.CONTRACT_PENDING_VALIDATION ||
        item.statusOfCandidatOnMission === CandidatStatusOnMission.ACCEPTED
    );
  }

  openContractPreview() {
    const pending = this.data.candidatesApplied?.find(
      c =>
        c.statusOfCandidatOnMission === CandidatStatusOnMission.CONTRACT_PENDING_VALIDATION ||
        c.statusOfCandidatOnMission === CandidatStatusOnMission.ACCEPTED || 
        c.statusOfCandidatOnMission === CandidatStatusOnMission.ACCEPTED_AND_SIGNED_BY_BOTH
    );
    if (!pending) return;

    // Fetch fresh mission data before opening the dialog
    this.loadingContract = true;
    this.missionService.getMissionById(String(this.data.id)).subscribe({
      next: (freshMission: any) => {
        this.loadingContract = false;
        const dialogRef = this.dialog.open(ContractPreviewDialogComponent, {
          width: '95vw',
          maxWidth: '1400px',
          height: '92vh',
          panelClass: 'contract-preview-panel',
          autoFocus: false,
          data: {
            missionId: this.data.id,
            candidatId: pending.candidatId,
            mission: freshMission
          }
        });

        dialogRef.afterClosed().subscribe((res: any) => {
          if (res?.missionSent || res?.dispositionSent || res?.sent) {
            this.missionService.toggleRefreshMission();
          }
        });
      },
      error: () => {
        this.loadingContract = false;
        // Fallback: open with existing data
        const dialogRef = this.dialog.open(ContractPreviewDialogComponent, {
          width: '95vw',
          maxWidth: '1400px',
          height: '92vh',
          panelClass: 'contract-preview-panel',
          autoFocus: false,
          data: {
            missionId: this.data.id,
            candidatId: pending.candidatId,
            mission: this.data
          }
        });

        dialogRef.afterClosed().subscribe((res: any) => {
          if (res?.missionSent || res?.dispositionSent || res?.sent) {
            this.missionService.toggleRefreshMission();
          }
        });
      }
    });
  }

  get totallyValidated() {
    if (['VALIDATED', 'INVALIDATED'].includes(this.data.missionStatus)) {
      return this.data.missionStatus == 'VALIDATED' ? 'Validée' : 'Validation refusée';
    }
    return false;
  }

  openMissionDetails() {
    const dialog = this.dialog.open(MissionDetailsComponent, {
      width: '666px',
      height: '100vh',
      position: { right: '0' },
      backdropClass: 'bg-transparent',
      data: { ...this.data, etbId: this.route.snapshot.params['establishmentId'] ?? 0, hideCandidatures: this.column?.detailsOnly },
    });
    dialog.afterClosed().subscribe((value) => {
    });
  }

  sendReminder() {
    const dialogRef = this.dialog.open(CustomDialogComponent, {
      data: {
        title: this.statusCandidateAccepted === CandidatStatusOnMission.ACCEPTED ? EtablissementData.mission.signContractReminder.title : this.statusCandidateAccepted === CandidatStatusOnMission.WAITING_FOR_ESTABLISHMENT_VALIDATION ? EtablissementData.mission.validateMissionReminder.title : null,
        description: this.statusCandidateAccepted === CandidatStatusOnMission.ACCEPTED ? EtablissementData.mission.signContractReminder.description : this.statusCandidateAccepted === CandidatStatusOnMission.WAITING_FOR_ESTABLISHMENT_VALIDATION ? EtablissementData.mission.validateMissionReminder.description : null,
        primaryLabel: EtablissementData.mission.signContractReminder.primaryButton,
        secondaryLabel: EtablissementData.mission.signContractReminder.secondButton,
      },
      maxWidth: '464px',
      autoFocus: false,
    });
    dialogRef.afterClosed().subscribe((res: any) => {
      if (res) {
        const reminderRequest = {
          missionId: this.data.id,
          cibleId: this.statusCandidateAccepted === CandidatStatusOnMission.ACCEPTED ? this.route.snapshot.params['candidatId'] : this.statusCandidateAccepted === CandidatStatusOnMission.WAITING_FOR_ESTABLISHMENT_VALIDATION ? this.data.etablissementId : 0,
          type: this.statusCandidateAccepted === CandidatStatusOnMission.ACCEPTED ? "CANDIDATE" : this.statusCandidateAccepted === CandidatStatusOnMission.WAITING_FOR_ESTABLISHMENT_VALIDATION ? "ESTABLISHMENT" : "",
          statusOnMission: this.statusCandidateAccepted === CandidatStatusOnMission.ACCEPTED ? CandidatStatusOnMission.ACCEPTED : this.statusCandidateAccepted === CandidatStatusOnMission.WAITING_FOR_ESTABLISHMENT_VALIDATION ? CandidatStatusOnMission.WAITING_FOR_ESTABLISHMENT_VALIDATION : null
        }
        if (this.statusCandidateAccepted === CandidatStatusOnMission.ACCEPTED) {
          this.missionService.sendSignContractReminder(reminderRequest).subscribe({
            next: () => {
              this.dialog.closeAll();
              this.missionService.toggleRefreshMission();
            },
            error: (err) => {
            }
          });
        }
        if (this.statusCandidateAccepted === CandidatStatusOnMission.WAITING_FOR_ESTABLISHMENT_VALIDATION) {
          this.missionService.sendValidateMissionReminder(Number(this.route.snapshot.params['candidatId']), reminderRequest).subscribe((res) => {
            this.missionService.toggleRefreshMission();
          });
        }
      }
    });

  }

  getMotifRefus() {
    this.missionService.findMotifRefus(this.data.id, Number(this.route.snapshot.params['candidatId']), this.data.etablissementId).subscribe({
      next: (motif: any) => {
        this.dialog.closeAll();
        const dialogRef = this.dialog.open(CustomDialogComponent, {
          data: {
            title: EtablissementData.mission.motifRefus.title,
            description: motif.motifsRefus,
            primaryLabel: EtablissementData.mission.motifRefus.primaryButton,
            secondaryLabel: '',
          },
          maxWidth: '464px',
          autoFocus: false,
        });

      },
      error: (err) => {
      }
    })
  }
  isTotallyAffected() {
    return this.data.candidatesApplied.some(item => item.statusOfCandidatOnMission === CandidatStatusOnMission.ACCEPTED_AND_SIGNED_BY_BOTH);
  }

  cancelPendingMission() {
    const dialogRef = this.dialog.open(CustomDialogComponent, {
      data: {
        title: EtablissementData.mission.cancelPendingMission.title,
        description: EtablissementData.mission.cancelPendingMission.description,
        primaryLabel: EtablissementData.mission.cancelPendingMission.primaryButton,
        secondaryLabel: EtablissementData.mission.cancelPendingMission.secondButton
      },
      maxWidth: '464px',
      autoFocus: false,
    });
    dialogRef.afterClosed().subscribe((res: any) => {
      if (res) {
        this.loading = true;
        this.missionService.cancelPendingMission(this.data.id).subscribe({
          next: () => { this.loading = false; this.missionService.toggleRefreshMission(); },
          error: () => { this.loading = false; },
        });
      }
    });
  }

  openMissionCandidacies() {
    if (this.availableCandidates.length == 0) {
      this.candidatService.getOnlyAvailbleCandidats(this.data.id).subscribe(
        (candidates: CandidatesApplied[]) => {
          this.availableCandidates = candidates;
          this.dialog.open(this.missionCandidaciesDialog, {
            width: '666px',
            height: '100vh',
            position: { right: '0' },
            backdropClass: 'bg-transparent'
          });
        },
        (error) => {
          console.error('Failed to fetch candidates', error);
        }
      );
    } else {
      this.dialog.open(this.missionCandidaciesDialog, {
        width: '666px',
        height: '100vh',
        position: { right: '0' },
        backdropClass: 'bg-transparent'
      });
    }
  }

  openMissionAllCandidacies() {
    if (this.availableCandidates.length == 0) {
      this.candidatService.getOnlyAvailbleCandidats(this.data.id).subscribe(
        (candidates: CandidatesApplied[]) => {
          this.availableCandidates = candidates;
          const dialogRef = this.dialog.open(this.missionAllCandidaciesDialog, {
            width: '666px',
            height: '100vh',
            position: { right: '0' },
            backdropClass: 'bg-transparent'
          });
        },
        (error) => {
          console.error('Failed to fetch candidates', error);
        }
      );
    } else {
      const dialogRef = this.dialog.open(this.missionAllCandidaciesDialog, {
        width: '666px',
        height: '100vh',
        position: { right: '0' },
        backdropClass: 'bg-transparent'
      });
    }
  }

  goTo(): void {
    const etbId = this.route.snapshot.params['establishmentId'] ?? 0;
    this.router.navigate([`/agency/mission-by-establishment/${etbId}/${this.data.id}`]);
  }

  navigateToFragmentedMission(): void {
    this.router.navigate(['/agency/fragmented-mision', this.data.id]);
  }

  deleteMission() {
    const dialogRef = this.dialog.open(CustomDialogComponent, {
      data: {
        title: EtablissementData.mission.deleteMission.title,
        description: EtablissementData.mission.deleteMission.description,
        primaryLabel: EtablissementData.mission.deleteMission.primaryButton,
        secondaryLabel: EtablissementData.mission.deleteMission.secondButton,
      },
      maxWidth: '464px',
      autoFocus: false,
    });
    dialogRef.afterClosed().subscribe((res: any) => {
      if (res) {
        this.loading = true;
        this.missionService.deleteDraft(this.data.id).subscribe({
          next: () => { this.loading = false; this.missionService.toggleRefreshMission(); },
          error: () => { this.loading = false; },
        });
      }
    });
  }



  protected readonly CandidatStatusOnMission = CandidatStatusOnMission;
  protected readonly MissionStatusEnum = MissionStatusEnum;
}
